import { useState } from 'react';
import { useSelector } from 'react-redux';
import service from 'services';
import selectors from 'state/selectors';
import utils from 'utils';
import {
  DocumentHistoryResponse,
  GetMailDocumentHistory,
  GetWfHistoryResponse,
  Group,
  WfDefinitionEngineTask,
} from 'models/response';

interface RowsProps {
  dateAndTime?: number;
  sender: string;
  typology?: number;
  typeDescription?: string;
  rejectionReason?: string;
  logNotes?: string;
  causal?: string;
  recipient?: Group[];
}

export const useDocumentData = () => {
  const t = utils.intl.useTranslator();
  const [tableRows, setTableRows] = useState<RowsProps[]>([]);
  const [historyRows, setHistoryRows] = useState<DocumentHistoryResponse[] | GetMailDocumentHistory[] | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<RowsProps | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [tasks, setTasks] = useState<WfDefinitionEngineTask[]>([]);

  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const activeDocument = useSelector(selectors.documents.selectActiveDocument);
  const userPreference = useSelector(selectors.user.getUserPreference);
  const notify = utils.app.notify;

  const setRowsContent = (data: GetWfHistoryResponse[]) => {
    return data.map((item) => ({
      dateAndTime: item.sendDate,
      sender: item.sender.name,
      typeDescription: item.typeDescription,
      rejectionReason: item.rejectionReason,
      logNotes: item.note,
      causal: item.causalCode,
      wfDefinition: item.wfDefinition,
      recipient: item.recipient,
    }));
  };

  const fetchData = async (channelOverride?: number) => {
    try {
      if (!protocol) return;

      const { data } = await service.getWfHistory(protocol);
      const { data: wfTasks } = await service.getCurrentStructureOfWorkflow(protocol);

      setTasks(wfTasks?.tasks ?? []);
      setTableRows(setRowsContent(data));

      const channel = channelOverride ?? activeDocument?.channel ?? activeDocument?.incChannel;

      if (channel === undefined || channel === null) {
        utils.app.notify('warning', t('document-history-no-channel'));
        return;
      }

      let history;
      if (channel === 1) {
        const { data: mailHistory } = await service.getMailDocumentHistory(protocol);
        history = mailHistory;
      } else {
        const { data: docHistory } = await service.getDocumentHistory(protocol);
        history = docHistory;
      }

      setHistoryRows(history);
    } catch (err) {
      console.error(err);
      notify('fail', t(err as string));
    }
  };

  return {
    tableRows,
    historyRows,
    selectedDocument,
    setSelectedDocument,
    selectedGroup,
    setSelectedGroup,
    tasks,
    fetchData,
    activeDocument,
    userPreference,
  };
};
